import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { PieTooltipProps } from "@nivo/pie/dist/types/types";
import { OLPaper } from "OLPaper";
import { ReactComponent as CircleIcon } from "components/Icons/circle.svg";
import { ReactComponent as OpenLoyaltyLogo } from "components/Icons/openLoyaltyIcon.svg";
import { OLTableActions } from "components/Table/OLTable";
import { ExternalLinkTooltip } from "components/Tooltips/ExternalLinkTooltip";
import {
  CircleGraphItem,
  DonutChart,
} from "components/UsersPercentageChart/DonutChart";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import React from "react";
import { formatNumberValue } from "utilities/numbers";
import { ReportContextMenu } from "./ReportContextMenu";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    root: {
      padding: theme.spacing(12, 0, 0),
      overflow: "visible",
      height: "100%",
    },
    DonutChartContainer: {
      position: "relative",
      height: "240px",
      marginBottom: theme.spacing(14),
    },
    DonutChartText: {
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      gap: "5px",
      justifyContent: "center",
      left: "50%",
      position: "absolute",
      textAlign: "center",
      top: "50%",
      transform: "translate(-50%,-50%)",
      verticalAlign: "middle",
      "& div": {
        color: theme.gray800,
        fontSize: 24,
        minWidth: "100%",
        textAlign: "center",
      },
      "& span": {
        color: theme.gray400,
        fontSize: 14,
        opacity: "0.5",
      },
    },
    ItemsContainer: {
      borderBottom: `1px solid ${theme.gray50}`,
      borderTop: `1px solid ${theme.gray50}`,
      marginTop: theme.spacing(12),
      maxHeight: theme.spacing(81),
      overflow: "auto",
    },
    itemsTable: {
      alignItems: "center",
      display: "flex",
      height: theme.spacing(20),
      padding: theme.spacing(0, 12),
      whiteSpace: "nowrap",
      width: "100%",
      "& + &": {
        borderTop: `1px solid ${theme.gray50}`,
      },
    },
    bottomSection: {
      alignItems: "center",
      color: theme.gray400,
      display: "flex",
      justifyContent: "space-between",
      minHeight: theme.spacing(24),
      padding: theme.spacing(8, 12),
    },
    tableLabel: {
      marginRight: theme.spacing(2),
      maxWidth: "70%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    tableValue: {
      marginLeft: "auto",
      marginRight: theme.spacing(4),
    },
    loaderContainer: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(12),
    },
  }),
);
type Props = {
  centralText?: {
    number?: number;
    text?: string;
    tooltip?: string;
  };
  contextActions?: OLTableActions[];
  items: CircleGraphItem[];
  tooltipElement?: React.FC<PieTooltipProps<CircleGraphItem>>;
  isLoading: boolean;
};
export function CircleGraph({
  centralText,
  contextActions,
  items,
  tooltipElement,
  isLoading,
}: Props) {
  const classes = useStyles();
  const theme: OLTheme = useTheme();

  return (
    <OLPaper className={classes.root}>
      {isLoading ? (
        <Box className={classes.loaderContainer}>
          <OpenLoyaltyLogo />
          <CircularProgress />
        </Box>
      ) : (
        <>
          <div className={classes.DonutChartContainer}>
            <DonutChart
              data={items}
              innerRadius={0.72}
              tooltipElement={tooltipElement}
            />
            {centralText && (
              <div className={classes.DonutChartText}>
                {centralText?.number && (
                  <div>{formatNumberValue(centralText.number)}</div>
                )}
                {centralText?.text && <span>{centralText.text}</span>}
                {centralText?.tooltip && (
                  <ExternalLinkTooltip name={centralText.tooltip} />
                )}
              </div>
            )}
          </div>
          <div className={classes.ItemsContainer}>
            {items?.map((item: CircleGraphItem, index: number) => (
              <div className={classes.itemsTable} key={index}>
                <CircleIcon
                  style={{
                    color: item.color,
                    fontSize: 12,
                    height: 12,
                    marginRight: theme.spacing(4),
                    width: 12,
                  }}
                />
                <span className={classes.tableLabel}>{item.label}</span>
                <span className={classes.tableValue}>{item.value}</span>
              </div>
            ))}
          </div>
          {contextActions && (
            <div className={classes.bottomSection}>
              <ReportContextMenu actions={contextActions} />
            </div>
          )}
        </>
      )}
    </OLPaper>
  );
}
